<template>
  <!-- Loading Modal -->
  <div v-if="isLoading" class="modal-overlay">
      <div class="modal">
        <p>Loading data...</p>
        <div class="spinner"></div>
      </div>
  </div>
  <div class="cl-page">
  <div class="cl-pageheader">
  <div class="cl-pageheader-title">Supplemental Report Management</div>
  <div class="RL_note">List of red cards issued by the referee. Click on a card for more details.</div>

  <div class="gridWrapper">
    <div class="gridContainer">
       <div v-for="game in groupedMisconductEvents" :key="game.GameNumber" class="gridGame">
          <div class="gridItem gamedetails">
              <div>{{ game.GameDate }} ... [Game: {{ game.GameNumber }}] ... [{{ game.League }} - {{ game.GenderAge }}] ... {{ game.Teams }} ... REF: {{ game.REFName }}</div>
              <div class="gridHeaders">
                  <div class="gridItem time">Time</div>
                  <div class="gridItem offender">Offender</div>
                  <div class="gridItem team">Team</div>
                  <div class="gridItem player">Player</div>
                  <div class="gridItem incidenttype">Incident Type</div>
                  <div class="gridItem incident-description">Incident Description</div>
                  <div class="gridItem misc-item">Misconduct</div>
                  <div class="gridItem refereeaction">Referee Action</div>
                  <div class="gridItem actions"></div>
              </div>    
          </div>

          <div class="gridContent">
          <ul v-if="game.misconductEvents.length > 0">
              <li v-for="card in game.misconductEvents" :key="card.id" class="gridRow"
                  :class="{'yellow-card': card.RefereeAction === 'Yellow Card','red-card': card.RefereeAction === 'Red Card'}">
              <div class="gridItem time">{{card.Time}}'</div>
              <div class="gridItem offender">({{card.Offender}})</div>
              <div class="gridItem team">{{ card.Team }}</div>
              <div class="gridItem player">#{{card.PlayerNumber}} {{card.PlayerNameLast}}, {{ card.PlayerNameFirst }}</div>
              <div class="gridItem incidenttype">{{  card.IncidentType }}</div>
              <div class="gridItem description">{{ card.IncidentDescription }}</div>
              <div class="gridItem misc-item">{{ card.MisconductItem || 'No misconduct data' }}</div>
              <div class="gridItem refereeaction">{{ card.RefereeAction }}</div>
              <div class="gridItem actions">
                  <GeneratePDF :card="card">
                      <span class="material-icons pdf-icon"> picture_as_pdf </span>
                  </GeneratePDF>
              </div>
              </li>
          </ul>
          <p v-else>No misconduct data</p>
          </div>
      </div>
    </div>
  </div>
</div>
</div>
</template>

<script>
import GeneratePDF from "../components/GeneratePDF.vue";
import { misconductColRef, matchreportsColRef } from "../firebase";
import { query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { ref, onMounted } from "vue";

export default {
  name: 'Misconduct_Admin',
  components: { GeneratePDF },
  setup() {
      const isLoading = ref(true);
      const groupedMisconductEvents = ref([]);

      onMounted(async () => {
          await loadMisconductEvents();
          isLoading.value = false;
      });

      const loadMisconductEvents = async () => {
          try {
              console.log("Fetching misconduct reports...");

              const qRC = query(misconductColRef, where("RefereeAction", "in", ["Yellow Card", "Red Card","Purple Card","Double Purple Card"]));
              const qRCSnapshot = await getDocs(qRC);

              if (qRCSnapshot.empty) {
                  console.log("No misconduct reports found.");
                  return;
              }

              console.log(`Found ${qRCSnapshot.size} misconduct reports.`);

              const misconductDocs = qRCSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

              const matchReportPromises = misconductDocs.map(misconduct => {
                if (!misconduct.MatchReportID) {
                    console.warn(`Missing MatchReportID for misconduct ID: ${misconduct.id}`);
                    return null; // We'll filter out later
                }
                return getDoc(doc(matchreportsColRef, misconduct.MatchReportID));
              });

            // Filter out nulls before await Promise.all
            const validMatchReportPromises = matchReportPromises.filter(promise => promise !== null);
            const matchReports = await Promise.all(validMatchReportPromises);

            
            //  const matchReports = await Promise.all(matchReportPromises);

              const misconductReportsWithDetails = misconductDocs.map((misconduct, index) => {
                  const matchReportDoc = matchReports[index];

                  if (!matchReportDoc.exists()) {
                      console.warn(`Match report not found for misconduct ID: ${misconduct.id}`);
                      return null;
                  }

                  const matchReportData = matchReportDoc.data();
                  const formattedDate = formatGameDate(matchReportData.GameDate);

                  return {
                      id: misconduct.id,
                      ...misconduct,
                      GameDate: formattedDate,
                      GameNumber: matchReportData.GameNumber,
                      League: matchReportData.League,
                      LeagueTypeSeason: matchReportData.LeagueTypeSeason,
                      TeamHome: matchReportData.TeamHome,
                      TeamAway: matchReportData.TeamAway,
                      ScoreHome: matchReportData.ScoreHome,
                      ScoreAway: matchReportData.ScoreAway,
                      YellowCardCount: matchReportData.YellowCardCount,
                      RedCardCount: matchReportData.RedCardCount,
                      PurpleCardCount: matchReportData.PurpleCardCount,
                      DoublePurpleCardCount: matchReportData.DoublePurpleCardCount,
                      GenderAge: matchReportData.GenderAge,
                      Teams: `${matchReportData.TeamHome} vs. ${matchReportData.TeamAway}`,
                      REFName: matchReportData.REFName,
                  };
              }).filter(event => event !== null);

              groupedMisconductEvents.value = groupAndSortMisconductEvents(misconductReportsWithDetails);

              console.log("Grouped misconduct events:", groupedMisconductEvents.value);
          } catch (error) {
              console.error("Error loading misconduct reports:", error);
          }
      };

      const formatGameDate = (gameDate) => {
          if (!gameDate) return "N/A";
          if (gameDate instanceof Object && typeof gameDate.toDate === "function") {
              const date = gameDate.toDate();
              return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`;
          } 
          return gameDate;
      };

      const groupAndSortMisconductEvents = (misconductReports) => {
          const groupedEventsMap = new Map();

          misconductReports.forEach(event => {
              if (!groupedEventsMap.has(event.GameNumber)) {
                  groupedEventsMap.set(event.GameNumber, {
                      GameNumber: event.GameNumber,
                      GameDate: event.GameDate,
                      League: event.League,
                      GenderAge: event.GenderAge,
                      Teams: event.Teams,
                      REFName: event.REFName,
                      misconductEvents: [],
                  });
              }
              groupedEventsMap.get(event.GameNumber).misconductEvents.push(event);
          });

          const sortedGroupedEvents = Array.from(groupedEventsMap.values()).sort(
              (a, b) => new Date(b.GameDate) - new Date(a.GameDate)
          );

          sortedGroupedEvents.forEach(game => {
              game.misconductEvents.sort((a, b) => a.Time - b.Time);
          });

          return sortedGroupedEvents;
      };

      return {
          isLoading,
          groupedMisconductEvents,
      };
  },
};
</script>

  
<style scoped>
.cl-page {
  min-height: 100vh;
  background: radial-gradient(1200px 600px at 20% 0%, #eac891 0%, #eac891 40%, #f2d9ad 100%);
}
.cl-pageheader {
  position: fixed;
  top: 80px; left:0px;  height: 60px; width: 100%;
  margin: 00px; padding: 10px;  
  border-top: 5px; border-top: solid;  border-top-color: #02b8cc;
  border-bottom: 1px;  border-bottom: solid;  border-bottom-color: #32e3f7;
  background-color: #e78d58;  color: rgb(0, 0, 0);
}
.cl-pageheader-title {
  left:140px;
  color: rgb(0, 0, 0);
  font-size: x-large; font-weight: bold;
}
.gridWrapper * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
.RL_note {
    position: fixed;
    top: 145px;
    left: 40px;
  }
.gridContainer {
    position: fixed;
    top: 180px;
    margin-left: 30px;
    margin-right: 20px;
    width: 1800px;
    max-width: 1800px;
    overflow-y: auto;
    scroll-behavior: smooth;
    font-size: 14px;
    height: 70vh;
  }
  /* Add 30px padding-bottom to each grid game */
.gridGame {
  padding-bottom: 20px; /* Adds 30px space at the bottom of each game grid */
}
.gridItem.gamedetails {
    margin-left: 10px;
    font-size: 20px; font-weight: bold; color:#080032;
    background-color: #92e0f6; /* Light grey background */
    padding-top: 10px;
    padding-left: 5px;
}
  .gridHeaders {
    display: grid;
    grid-template-columns: 50px 70px 200px 200px 125px 500px 350px 150px 80px;
    gap: 5px;
    padding: 2px;
    background-color: #92e0f6;
    margin-bottom: 1px;
    font-size: 11px; font-weight: normal; font-style: italic;
  }
.gridContent ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }
.gridRow {
    display: grid;
    grid-template-columns: 50px 70px 200px 200px 125px 500px 350px 150px 60px;
    gap: 5px;
    padding-top: 5px;
    margin-left: 20px; margin-bottom: 4px;
    border-radius: 10px;
    background: #fff;
    font-size: 12pt;
    height: auto;
  }
/* For Yellow Card */
.gridRow.yellow-card {
  background-color: rgb(251, 251, 208); /* Yellow background for Yellow Card */
}

/* For Red Card */
.gridRow.red-card {
  background-color: rgb(249, 224, 224); /* Red background for Red Card */
  color: rgb(0, 0, 0); /* White text for better contrast */
}
/* Align time in the center */
.gridItem.time {
  text-align: center; /* Center align the time text */
}
.gridItem {
    padding: 5px;
    text-align: left;
  }
.gridItem.actions {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 40px;
    gap: 10px;
  }
.material-icons {
    cursor: pointer;
    font-size: 18px;
    color: #888;
  }
.material-icons.pdf-icon {
  background-color: white;
  border-radius: 50%; /* Optional, if you want a rounded background */
  padding: 5px; /* Optional, adjust for spacing around the icon */
  color: #13033b; /* Keep the icon color if you want */
  }
.material-icons:hover {
    color: #005ce6;
  }
  .material-icons.delete:hover {
    color: red;
  }

  /* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Modal */
.modal {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 200px;
  text-align: center;
}

/* Spinner for loading animation */
.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

/* Spinner animation */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
  </style>
  